import 'twin.macro';
import { useCountryConfig } from 'queries/user/useCountryConfig';
import React, { useState } from 'react';

import { SidebarComponent } from './Sidebar';
import EmploiDuTempsComponent from '@teetsh/app/src/components/EDT/Loadable';
import { Onboarding } from './OnBoarding';
import { PdfPreviewHighlight } from './PdfPreviewHighlight';
import { EdtToolbar } from './Toolbar';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import ManualClassroomsProvider from '@teetsh/app/src/containers/App/context/ClassroomsContext/ManualClassroomsContext';
import { EmploiDuTemps } from '@teetsh/app/src/types/edt';
import useReorderEdtSeances from '@teetsh/app/src/queries/edt/useReorderEdtSeances';
import useCreateEDTEvent from '@teetsh/app/src/queries/edt/useCreateEDTEvent';
import { useUpdateEdtSeance } from '@teetsh/app/src/queries/edt/useUpdateEdtSeance';
import useDeleteEDTSeance from '@teetsh/app/src/queries/edt/useDeleteEDTSeance';
import useCreateTagJoin from '@teetsh/app/src/queries/tags/useCreateTagJoin';
import { useEDTDisplayConfig } from '../useEDTDisplayConfig';
import { useEdtDrawers } from './Toolbar/hooks/useEdtDrawers';
import { UpdateEDTInfoModal } from '../UpdateEDTInfoModal';

export const theDate = new Date(2017, 4, 23);

export default function EDT({
  emploiDuTemps,
  isGuest,
  isReadOnly,
  onDuplicateShareEDT,
  enableEventTemplate,
}: {
  emploiDuTemps: EmploiDuTemps;
  isGuest?: boolean;
  isReadOnly?: boolean;
  onDuplicateShareEDT?: () => void;
  enableEventTemplate: boolean;
}) {
  const { areOnboardingTutoEnabled } = useCountryConfig();
  const classrooms = useClassrooms();
  const { displayedClassroomsIds, setDisplayedClassroomsIds } =
    useEDTDisplayConfig(emploiDuTemps.id);
  const [currentDate, setCurrentDate] = useState<Date>(theDate);
  const { school, schoolyear } = useSchoolSchoolyear();
  const schoolId = school.id;
  const schoolyearId = schoolyear.id;
  const removeSeance = useDeleteEDTSeance(emploiDuTemps.id);
  const onReorderSeances = useReorderEdtSeances(emploiDuTemps.id);
  const updateEdtSeance = useUpdateEdtSeance(emploiDuTemps.id);
  const createEvent = useCreateEDTEvent(emploiDuTemps.id);
  const createTagJoin = useCreateTagJoin({
    schoolyearId: schoolyear.id,
    schoolId: school.id,
  });

  const classroomsToDisplay = classrooms.filter(c =>
    displayedClassroomsIds.includes(c.id),
  );

  // Drawer management for new toolbar
  const { drawerView, openDrawer, setDrawerView } = useEdtDrawers();

  return (
    <div tw="h-full pt-4">
      {areOnboardingTutoEnabled && !isGuest && <Onboarding />}
      <PdfPreviewHighlight />
      <SidebarComponent
        classrooms={classrooms}
        displayedClassrooms={displayedClassroomsIds}
        emploiDuTemps={emploiDuTemps}
        onChangeDisplayedClassrooms={setDisplayedClassroomsIds}
        externalDrawerView={drawerView}
        onExternalDrawerViewChange={setDrawerView}
      />
      <div style={{ flex: '1' }}>
        <EdtToolbar
          edtId={emploiDuTemps.id}
          emploiDuTempsName={emploiDuTemps.name}
          emploiDuTempsSubtitle={emploiDuTemps.subtitle || ''}
          isReadOnly={isReadOnly}
          onDuplicateShareEDT={onDuplicateShareEDT}
          onOpenDrawer={openDrawer}
          date={currentDate}
          displayedClassrooms={displayedClassroomsIds}
          emploiDuTemps={emploiDuTemps}
          isGuest={isGuest}
        />
        <ManualClassroomsProvider classrooms={classroomsToDisplay}>
          <EmploiDuTempsComponent
            classroomsOrg={school.classroomsOrg}
            onChangeDate={date => setCurrentDate(date)}
            emploiDuTemps={emploiDuTemps}
            schoolyearId={schoolyearId}
            schoolId={schoolId}
            onCreateEvents={evts =>
              Promise.all(evts.map(evt => createEvent(evt)))
            }
            onRemoveSeance={removeSeance}
            onReorderSeances={onReorderSeances}
            onUpdateSeance={updateEdtSeance}
            onAddTagToClassroom={createTagJoin}
            isReadOnly={isReadOnly}
            enableEventTemplate={enableEventTemplate}
          />
        </ManualClassroomsProvider>
      </div>
      <UpdateEDTInfoModal />
    </div>
  );
}
